/**
 * Las reglas visuales de una forma: contraste, caja de texto y alto mínimo.
 *
 * Son funciones puras y sin estado, como `edgeGeometry.ts`: no tocan el store
 * ni el DOM de la app, así que la vista (`ShapeView`), la capa de texto
 * (`canvasText.ts`) y su editor pueden usar exactamente las mismas medidas. Que
 * las tres salgan de acá es justamente lo que evita que el texto se mueva al
 * entrar o salir de edición.
 *
 * La tipografía, la medición y el maquetado NO están acá: son de todo el texto
 * del lienzo y viven en `canvasText.ts`. Acá queda sólo lo que es propio de una
 * forma —su geometría y el contraste contra su relleno—, que es lo que
 * `textStyle.ts` resuelve distinto para un nodo de texto suelto.
 *
 * Ver `docs/plan-formas.md` §D5 y §D7.
 */

import { type ShapeKind } from "../model/types.ts";
import { type Box, type TextCover, measureTextHeight } from "./canvasText.ts";

export { type Box } from "./canvasText.ts";

/** Aire entre el texto y el borde de la caja inscrita, en px de mundo. */
export const SHAPE_PAD = 12;
/** Radio de `rect`. Con relleno sólido se lee mejor que la esquina viva, y es
 *  lo que hace innecesario un `roundRect` aparte. */
export const SHAPE_RECT_RADIUS = 8;

/** Tamaño de una forma recién creada de un solo clic (sin arrastrar). */
export const SHAPE_DEFAULT_W = 200;
export const SHAPE_DEFAULT_H = 110;
/**
 * Ninguna forma puede quedar más chica que esto, ni dibujándola ni al
 * redimensionarla: por debajo no entra ni una palabra.
 *
 * Los dos valen lo MISMO a propósito: si no, un arrastre con Shift dejaría de
 * ser 1:1 justo al llegar al mínimo, porque cada eje se frenaría en un número
 * distinto.
 */
export const SHAPE_MIN_W = 44;
export const SHAPE_MIN_H = 44;

const WHITE = "#ffffff";
const INK = "#1a1a1f";

// ---- contraste -------------------------------------------------------------

/** Luminancia relativa sRGB (WCAG 2.1 §relative luminance). */
function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const channel = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return (
    0.2126 * channel((n >> 16) & 255) +
    0.7152 * channel((n >> 8) & 255) +
    0.0722 * channel(n & 255)
  );
}

/**
 * Umbral de luminancia a partir del cual el relleno se considera "claro" y el
 * texto pasa a casi negro. Por debajo, el texto es siempre blanco.
 *
 * Es deliberadamente alto (0.5): con la paleta actual (`CanvasToolbar.COLORS`,
 * luminancias 0.03–0.23) las seis formas quedan con texto blanco, que es el
 * look pedido. El negro sólo aparece con rellenos muy claros (amarillos,
 * pasteles, blanco) donde el blanco sería ilegible.
 */
const LIGHT_FILL_THRESHOLD = 0.5;

/**
 * El color del texto encima de un relleno: blanco salvo relleno muy claro.
 *
 * No se guarda en el modelo — se calcula. Así, agregar un color a la paleta no
 * obliga a tocar nada más, y ninguna forma puede quedar guardada con un texto
 * ilegible encima.
 */
export function textColorFor(fill: string): string {
  return luminance(fill) > LIGHT_FILL_THRESHOLD ? INK : WHITE;
}

// ---- caja de texto ---------------------------------------------------------

const SQRT2 = Math.SQRT2;

/**
 * Qué fracción del rect envolvente ocupa el rectángulo inscrito en la
 * geometría. Es de acá que sale que el texto de un rombo no se escape por las
 * puntas: en un rombo sólo la mitad central tiene relleno debajo.
 */
function inscribedRatio(kind: ShapeKind): number {
  switch (kind) {
    case "ellipse":
      return 1 / SQRT2;
    case "diamond":
      return 0.5;
    default:
      // `rect` y `pill` usan la caja entera; las puntas redondeadas de la
      // píldora se comen poco y el padding ya las cubre.
      return 1;
  }
}

/**
 * La caja donde va el texto, en coordenadas locales de la forma (0,0 = su
 * esquina superior izquierda). Puede quedar en cero si la forma es muy chica;
 * quien la use tiene que tolerarlo.
 */
export function textBox(kind: ShapeKind, width: number, height: number): Box {
  const r = inscribedRatio(kind);
  const w = Math.max(0, width * r - SHAPE_PAD * 2);
  const h = Math.max(0, height * r - SHAPE_PAD * 2);
  return { x: (width - w) / 2, y: (height - h) / 2, width: w, height: h };
}

/** El ancho útil para el texto de una forma de este ancho. */
export function textWidthFor(kind: ShapeKind, width: number): number {
  return Math.max(0, width * inscribedRatio(kind) - SHAPE_PAD * 2);
}

// ---- hit-test --------------------------------------------------------------

/** Punto dentro de un rectángulo redondeado, dado su medio-ancho/alto y radio. */
function insideRoundRect(dx: number, dy: number, halfW: number, halfH: number, r: number): boolean {
  const ox = Math.max(dx - (halfW - r), 0);
  const oy = Math.max(dy - (halfH - r), 0);
  return ox * ox + oy * oy <= r * r;
}

/**
 * ¿Cae este punto de mundo DENTRO de la forma, y no sólo dentro de su rectángulo
 * envolvente?
 *
 * Importa cuando hay cajas pegadas: en un rombo, casi la mitad del rectángulo
 * está vacía, y con el relleno sólido "hago clic donde no hay nada y se
 * selecciona igual" se lee como un error. Las manijas de redimensionar siguen
 * estando en las esquinas del envolvente, pero se prueban ANTES que esto
 * (`resizeDirAt` en CanvasView), así que redimensionar no se ve afectado.
 */
export function hitsShape(
  kind: ShapeKind,
  x: number,
  y: number,
  width: number,
  height: number,
  px: number,
  py: number,
): boolean {
  if (px < x || px > x + width || py < y || py > y + height) return false;
  if (width <= 0 || height <= 0) return false;
  const halfW = width / 2;
  const halfH = height / 2;
  const dx = Math.abs(px - (x + halfW));
  const dy = Math.abs(py - (y + halfH));

  switch (kind) {
    case "ellipse":
      return (dx / halfW) ** 2 + (dy / halfH) ** 2 <= 1;
    case "diamond":
      return dx / halfW + dy / halfH <= 1;
    case "pill":
      return insideRoundRect(dx, dy, halfW, halfH, Math.min(halfW, halfH));
    default:
      return insideRoundRect(
        dx,
        dy,
        halfW,
        halfH,
        Math.min(SHAPE_RECT_RADIUS, Math.min(width, height) / 4),
      );
  }
}

// ---- medición --------------------------------------------------------------

/**
 * El alto mínimo que necesita una forma para que su texto entre, dado su ancho.
 *
 * Es la inversa de `textBox`: se mide el texto al ancho útil y se convierte esa
 * altura de texto de vuelta a altura de forma. El ancho es de quien la
 * redimensiona; el alto lo pide el texto (plan §D7.2).
 */
export function fitHeight(kind: ShapeKind, width: number, text: string): number {
  // Una caja vacía no tiene nada que contener: sólo el mínimo. Sin esto, dibujar
  // o redimensionar con Shift no daba un cuadrado ni un círculo — el alto se
  // estiraba para dejar entrar una línea que no existe, y rompía la proporción.
  if (text === "") return SHAPE_MIN_H;
  const h = measureTextHeight(text, textWidthFor(kind, width)) + SHAPE_PAD * 2;
  return Math.max(SHAPE_MIN_H, Math.ceil(h / inscribedRatio(kind)));
}

/**
 * La silueta con la que una forma tapa el texto de lo que tiene debajo. Es la
 * misma que pinta `paintShapePath`: un rombo tapa sólo su rombo, y lo que asoma
 * por sus puntas se sigue leyendo.
 */
export function shapeCover(
  card: { x: number; y: number; width: number; height: number },
  kind: ShapeKind,
): TextCover {
  const { x, y, width, height } = card;
  if (kind === "ellipse" || kind === "diamond") return { kind, x, y, width, height, radius: 0 };
  const radius =
    kind === "pill"
      ? Math.min(width, height) / 2
      : Math.min(SHAPE_RECT_RADIUS, Math.min(width, height) / 4);
  return { kind: "rect", x, y, width, height, radius };
}
